import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { GoogleLog, GoogleResource, GoogleStatus } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/** Google bağlantı/kaynak hata kodunun metni: istemci kodları `admin.google.errors`, uygulama kodları `errors`. */
const LINK_ERRORS = [
  'token_revoked',
  'userinfo_failed',
  'resource_not_accessible',
  'insufficient_scope',
  'permission_denied',
  'api_not_enabled',
  'unauthorized',
  'http_error',
  'quota_exceeded',
  'network',
  'timeout',
] as const;
export const googleErrorText = (t: ReturnType<typeof useT>, code: string) =>
  (LINK_ERRORS as readonly string[]).includes(code) ? t(`admin.google.errors.${code}`) : t(`errors.${code}`);

/**
 * Google bağlantı merkezi (F5-09): OAuth istemcisi durumu ve dönüş adresi, Google hesabı bağlantısı (hizmet
 * başına izin), mağaza başına mülk/hesap seçimi, bağlantı testi, kaldırma ve günlük.
 */

type ServiceKey = GoogleStatus['services'][number]['key'];
const SERVICES: ServiceKey[] = [
  'search_console',
  'analytics',
  'merchant_center',
  'google_ads',
  'business_profile',
];

export function GooglePage() {
  const t = useT();
  const q = useQuery({ queryKey: ['google'], queryFn: () => api.get<GoogleStatus>('/v1/admin/google') });
  // Google'dan dönüşte sonuç adres çubuğunda gelir; bir kez gösterilip adresten silinir.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ok = p.get('sonuc');
    const err = p.get('hata');
    if (!ok && !err) return;
    if (ok === 'baglandi') toast.success(t('admin.google.connected'));
    if (err) toast.error(t(`errors.${err}`));
    window.history.replaceState(null, '', window.location.pathname);
  }, [t]);
  return (
    <PageFrame helpKey="page.google">
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <ClientSection d={q.data} />
          <AccountSection d={q.data} />
          {q.data.connection && <ResourcesSection d={q.data} />}
          <LogsSection />
        </>
      )}
    </PageFrame>
  );
}

function ClientSection({ d }: { d: GoogleStatus }) {
  const t = useT();
  const ok = d.client.status === 'LIVE';
  return (
    <Section
      title={t('admin.google.clientTitle')}
      description={t('admin.google.clientLead')}
      action={<HelpTip k="google.client" />}
    >
      <p className="flex flex-wrap items-center gap-2">
        <Badge tone={ok ? 'moss' : 'amber'}>
          {ok ? t('admin.google.clientOk') : t('integrationStatus.NOT_CONFIGURED')}
        </Badge>
        {!ok && <span className="text-sm text-ink-2">{t('admin.google.clientMissing')}</span>}
        <Link to="/entegrasyonlar" className="text-sm text-pine hover:underline">
          {t('admin.google.toIntegrations')}
        </Link>
      </p>
      <Field
        label={t('admin.google.redirectUri')}
        help={<HelpTip k="google.redirect_uri" />}
        className="mt-4 max-w-2xl"
      >
        {(p) => (
          <div className="flex gap-2">
            <Input {...p} readOnly value={d.client.redirectUri} className="font-mono text-[0.8125rem]" />
            <Button
              type="button"
              onClick={() =>
                void navigator.clipboard
                  .writeText(d.client.redirectUri)
                  .then(() => toast.success(t('admin.google.copied')))
              }
            >
              {t('admin.google.copy')}
            </Button>
          </div>
        )}
      </Field>
    </Section>
  );
}

function AccountSection({ d }: { d: GoogleStatus }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canManage = can('org.integration.manage');
  const [selected, setSelected] = useState<ServiceKey[]>(() => {
    const granted = d.services.filter((s) => s.granted).map((s) => s.key);
    return granted.length ? granted : SERVICES;
  });
  const connect = useApiMutation(
    () => api.post<{ url: string }>('/v1/admin/google/connect', { services: selected }),
    { onSuccess: (r) => window.location.assign(r.url) },
  );
  const test = useApiMutation(() => api.post<{ ok: boolean }>('/v1/admin/google/test', {}), {
    invalidate: [['google'], ['google-logs']],
    success: (r) => (r.ok ? t('admin.google.testOk') : t('admin.google.testFailed')),
  });
  const c = d.connection;
  const clientOk = d.client.status === 'LIVE';
  return (
    <Section
      title={t('admin.google.accountTitle')}
      description={t('admin.google.accountLead')}
      action={<HelpTip k="google.connect" />}
    >
      {c ? (
        <dl className="mb-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-ink-2">{t('admin.google.account')}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{c.accountEmail}</span>
            <Badge tone={c.status === 'active' ? 'moss' : 'rose'}>
              {t(`admin.google.status.${c.status}`)}
            </Badge>
            {c.lastError && <span className="text-ink-2">{googleErrorText(t, c.lastError)}</span>}
          </dd>
          <dt className="text-ink-2">{t('admin.google.connectedAt')}</dt>
          <dd>{formatDate(c.connectedAt, true)}</dd>
          <dt className="text-ink-2">{t('admin.google.lastChecked')}</dt>
          <dd>{formatDate(c.lastCheckedAt, true)}</dd>
        </dl>
      ) : (
        <p className="mb-4 text-sm text-ink-2">{t('admin.google.notConnected')}</p>
      )}
      <fieldset className="space-y-2" disabled={!canManage || !clientOk}>
        <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
          {c ? t('admin.google.permissionsTitle') : t('admin.google.chooseServices')}
          <HelpTip k="google.services" />
        </legend>
        <ul className="grid gap-2 sm:grid-cols-3">
          {d.services.map((s) => (
            <li key={s.key} className="rounded-md border border-line px-3 py-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-[var(--zt-pine)]"
                  checked={selected.includes(s.key)}
                  onChange={(e) =>
                    setSelected(e.target.checked ? [...selected, s.key] : selected.filter((x) => x !== s.key))
                  }
                />
                <span>
                  <span className="font-medium">{t(`admin.google.services.${s.key}`)}</span>
                  <span className="block text-ink-3">{t(`admin.google.serviceLeads.${s.key}`)}</span>
                  {c && (
                    <Badge tone={s.granted ? 'moss' : 'slate'} className="mt-1">
                      {s.granted ? t('admin.google.granted') : t('admin.google.notGranted')}
                    </Badge>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      {canManage && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            disabled={!clientOk || !selected.length}
            loading={connect.isPending}
            onClick={() => connect.mutate(undefined)}
          >
            {c ? t('admin.google.reconnect') : t('admin.google.connect')}
          </Button>
          {c && (
            <>
              <Button loading={test.isPending} onClick={() => test.mutate(undefined)}>
                {t('admin.google.test')}
              </Button>
              <HelpTip k="google.test" />
              <DisconnectButton />
            </>
          )}
        </div>
      )}
    </Section>
  );
}

function DisconnectButton() {
  const t = useT();
  const remove = useApiMutation(() => api.del<{ revoked: boolean }>('/v1/admin/google/connection'), {
    invalidate: [['google'], ['google-logs']],
    success: (r) => (r.revoked ? t('admin.google.disconnected') : t('admin.google.disconnectedLocal')),
  });
  return (
    <span className="flex items-center gap-1">
      <ConfirmButton
        message={t('admin.google.disconnectConfirm')}
        onConfirm={() => remove.mutateAsync(undefined)}
      >
        {t('admin.google.disconnect')}
      </ConfirmButton>
      <HelpTip k="google.disconnect" />
    </span>
  );
}

function ResourcesSection({ d }: { d: GoogleStatus }) {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <Section
      title={t('admin.google.resourcesTitle')}
      description={t('admin.google.resourcesLead')}
      action={<HelpTip k="google.resources" />}
    >
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {storeId && (
        <ul className="mt-3 divide-y divide-line rounded-md border border-line">
          {d.services.map((s) => (
            <ResourceRow
              key={`${storeId}-${s.key}`}
              storeId={storeId}
              service={s.key}
              granted={s.granted}
              link={d.links.find((l) => l.storeId === storeId && l.service === s.key) ?? null}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

function ResourceRow({
  storeId,
  service,
  granted,
  link,
}: {
  storeId: string;
  service: ServiceKey;
  granted: boolean;
  link: GoogleStatus['links'][number] | null;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [choosing, setChoosing] = useState(false);
  const [value, setValue] = useState('');
  const list = useQuery({
    queryKey: ['google-resources', service],
    queryFn: () => api.get<GoogleResource[]>(`/v1/admin/google/resources/${service}`),
    enabled: choosing && granted,
  });
  const save = useApiMutation(
    () => api.put(`/v1/admin/stores/${storeId}/google/${service}`, { resourceId: value }),
    {
      invalidate: [['google']],
      success: t('admin.google.linked'),
      onSuccess: () => setChoosing(false),
      silentFieldErrors: true,
    },
  );
  const unlink = useApiMutation(() => api.del(`/v1/admin/stores/${storeId}/google/${service}`), {
    invalidate: [['google']],
    success: t('admin.google.unlinked'),
  });
  const name = t(`admin.google.services.${service}`);
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{name}</p>
        {link ? (
          <p className="text-sm">
            {link.resourceName}
            <span className="block text-ink-3">
              {link.lastSyncAt
                ? t('admin.google.lastSync', { time: formatDate(link.lastSyncAt, true) })
                : t('admin.google.noSyncYet')}
              {link.lastError && ` · ${googleErrorText(t, link.lastError)}`}
            </span>
          </p>
        ) : (
          <p className="text-sm text-ink-3">
            {granted ? t('admin.google.notLinked') : t('admin.google.serviceNotGranted')}
          </p>
        )}
      </div>
      {can('org.integration.manage') && granted && !choosing && (
        <span className="flex gap-2">
          <Button size="sm" onClick={() => setChoosing(true)}>
            {link ? t('admin.google.change') : t('admin.google.choose')}
          </Button>
          {link && (
            <Button
              size="sm"
              variant="ghost"
              loading={unlink.isPending}
              onClick={() => unlink.mutate(undefined)}
            >
              {t('admin.google.unlink')}
            </Button>
          )}
        </span>
      )}
      {choosing && (
        <form
          className="flex w-full flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(undefined);
          }}
        >
          {list.isLoading ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <Field
              label={t('admin.google.resourceLabel', { service: name })}
              error={fieldError(save.error, 'resourceId')}
              className="min-w-64 flex-1"
            >
              {(p) => (
                <Select
                  {...p}
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    save.reset();
                  }}
                >
                  <option value="">{t('admin.google.pick')}</option>
                  {(list.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Button type="submit" variant="primary" disabled={!value} loading={save.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" onClick={() => setChoosing(false)}>
            {t('common.cancel')}
          </Button>
          {list.data && list.data.length === 0 && (
            <p className="w-full text-sm text-ink-2">{t('admin.google.noResources')}</p>
          )}
          {/* Liste alınamadıysa nedeni ve ne yapılacağı. */}
          {list.error instanceof ApiError && (
            <p role="alert" className="w-full text-sm text-rose">
              {list.error.title}
            </p>
          )}
          {/* Seçilen hesap okunamadıysa (ör. Google Ads Cloud projesi Test erişimli, F7-32) kalıcı açıklama. */}
          {save.error instanceof ApiError && save.error.errors.length === 0 && (
            <p role="alert" className="w-full text-sm text-rose">
              {save.error.title}
            </p>
          )}
        </form>
      )}
    </li>
  );
}

function LogsSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['google-logs'],
    queryFn: () => api.get<GoogleLog[]>('/v1/admin/google/logs'),
  });
  return (
    <Section
      title={t('admin.google.logsTitle')}
      description={t('admin.google.logsLead')}
      action={<HelpTip k="google.logs" />}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <EmptyState title={t('admin.google.noLogs')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.google.logsTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.google.colTime')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.google.colOperation')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.google.colOutcome')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.google.colDetail')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((l) => {
                const s = (l.summary ?? {}) as Record<string, unknown>;
                return (
                  <tr key={l.id} className="border-t border-line">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(l.createdAt, true)}</td>
                    <td className="px-3 py-2">{t(`admin.google.operations.${l.operation}`)}</td>
                    <td className="px-3 py-2">
                      <Badge tone={l.outcome === 'success' ? 'moss' : 'rose'}>
                        {t(`admin.google.outcomes.${l.outcome}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-ink-2">
                      {typeof s.error === 'string'
                        ? t('admin.google.logError', { code: s.error })
                        : typeof s.service === 'string'
                          ? t(`admin.google.services.${s.service}`)
                          : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
